import { bumpNote, createBlankDraft, mergeUrlMetadata, normalizeTags, noteFromDraft, transitionNoteStatus, type Note } from './note'
import { createId } from '../utils/id'
import type { NoteRepository } from '../data/db'
import type { NoteAsset } from '../assets/assetTypes'
import { APP_SETTINGS } from '../config/appSettings'

export interface NoteOperation {
  id: string
  kind: 'merge' | 'tags'
  createdAt: number
  count: number
  mergedNoteId?: string
  before: Note[]
  after: Note[]
  dismissed: boolean
  undone: boolean
}

export const operationPrefix = (scope: string) => `note-operations:v1:${encodeURIComponent(scope)}:`
export const operationMessage = (operation: NoteOperation) => operation.kind === 'merge'
  ? `Merged ${operation.count} notes into this one`
  : `Updated tags on ${operation.count} note${operation.count === 1 ? '' : 's'}`

export function concatenateNotes(notes: readonly Note[], title = ''): string {
  const body = notes.map(note => [note.manualTitle ? `# ${note.manualTitle}` : '', note.body].filter(Boolean).join('\n\n')).join('\n\n---\n\n')
  // The optional title is literal user text and belongs to the editable document.
  const heading = title.trim().replace(/[\r\n]+/g, ' ').replace(/([\\`*_{}\[\]()#+.!<>~|])/g, '\\$1')
  return heading ? `# ${heading}\n\n${body}` : body
}

export function planMerge(notes: Note[], deleteOriginals: boolean, deviceId: string, title = ''): NoteOperation {
  if (notes.length < 2 || new Set(notes.map(note => note.id)).size !== notes.length) throw new Error('Select at least two different notes.')
  if (notes.some(note => note.status === 'trash')) throw new Error('Restore trashed notes before merging them.')
  const draft = { ...createBlankDraft(), body: concatenateNotes(notes, title),
    annotation: notes.map(note => note.annotation).filter(Boolean).join('\n\n---\n\n'),
    userTags: normalizeTags(notes.flatMap(note => note.userTags)),
    assetIds: [...new Set(notes.flatMap(note => note.assetIds))] }
  const merged = noteFromDraft(draft, null, deviceId)
  merged.urls = mergeUrlMetadata(merged.urls, notes.flatMap(note => note.urls))
  return { id: createId(), kind: 'merge', createdAt: Date.now(), count: notes.length, mergedNoteId: merged.id,
    before: notes, after: [merged, ...(deleteOriginals ? notes.map(note => transitionNoteStatus(note, 'trash', deviceId)) : [])],
    dismissed: false, undone: false }
}

export function planTags(notes: Note[], remove: string[], add: string[], deviceId: string): NoteOperation {
  const removed = new Set(normalizeTags(remove))
  const after = notes.flatMap(note => {
    const userTags = normalizeTags([...note.userTags.filter(tag => !removed.has(tag)), ...add])
    return JSON.stringify(userTags) === JSON.stringify(note.userTags) ? [] : [{ ...bumpNote(note, deviceId), userTags }]
  })
  return { id: createId(), kind: 'tags', createdAt: Date.now(), count: notes.length,
    before: notes, after, dismissed: false, undone: false }
}

export async function copyMergeAssets(repository: NoteRepository, operation: NoteOperation, loadMaster?: (asset: NoteAsset) => Promise<Blob>): Promise<NoteAsset[]> {
  if (operation.kind !== 'merge') return []
  const merged = operation.after[0]!
  const assets: NoteAsset[] = []
  for (let index = 0; index < merged.assetIds.length; index += APP_SETTINGS.images.masterDownloadConcurrency) {
    assets.push(...await Promise.all(merged.assetIds.slice(index, index + APP_SETTINGS.images.masterDownloadConcurrency).map(async id => {
    let asset = await repository.getAsset(id)
    if (!asset || asset.state === 'deleted') throw new Error('An attachment is unavailable. Let the original device finish syncing, then retry.')
    if (!asset.processedMasterBlob && !asset.sourceBlob) {
      if (!loadMaster) throw new Error('An attachment is not downloaded yet. Connect to download the full image and retry.')
      const master = await loadMaster(asset)
      asset = { ...asset, processedMasterBlob: master, masterMimeType: master.type, processedSize: master.size }
    }
    return { ...asset, id: createId(), noteId: merged.id, masterStoragePath: null, thumbnailStoragePath: null,
      state: asset.processedMasterBlob ? 'ready_to_upload' as const : 'local' as const,
      errorStage: null, attempts: 0, nextAttemptAt: Date.now(), lastError: null, createdAt: Date.now(), modifiedAt: Date.now() }
  })))
  }
  merged.assetIds = assets.map(asset => asset.id)
  return assets
}

export async function commitNoteOperation(repository: NoteRepository, scope: string, operation: NoteOperation, assets: NoteAsset[] = []) {
  const before = new Map(operation.before.map(note => [note.id, note]))
  // Guard even untouched sources so the reviewed concatenation never silently goes stale.
  const changedIds = new Set(operation.after.map(note => note.id))
  const changes = operation.after.map(note => ({ note, expectedRevision: before.get(note.id)?.revisionId ?? null }))
  // Source revision checks share the same transaction without producing source revisions/outbox writes.
  await repository.commitLocalNotes(changes, {
    key: operationPrefix(scope) + operation.id, value: JSON.stringify(operation), expected: undefined,
  }, operation.before.filter(note => !changedIds.has(note.id)), assets)
}

export async function undoNoteOperation(repository: NoteRepository, scope: string, operation: NoteOperation, deviceId: string) {
  if (operation.undone) throw new Error('This operation was already undone.')
  const affectedIds = new Set([...operation.before, ...operation.after].map(note => note.id))
  const newer = (await repository.listNoteOperationReceipts(operationPrefix(scope)))
    .map(record => JSON.parse(record) as NoteOperation)
    .some(item => item.id !== operation.id && !item.undone && item.createdAt >= operation.createdAt
      && [...item.before, ...item.after].some(note => affectedIds.has(note.id)))
  if (newer) throw new Error('Undo the newer actions on these notes first, in Settings → Actions log.')
  const changes = []
  for (const after of operation.after) {
    const current = await repository.getNote(after.id)
    if (!current) throw new Error('An affected note is unavailable. Undo has made no changes.')
    const before = operation.before.find(note => note.id === after.id)
    let note: Note
    if (operation.kind === 'tags' && before) {
      const added = after.userTags.filter(tag => !before.userTags.includes(tag))
      const removed = before.userTags.filter(tag => !after.userTags.includes(tag))
      note = { ...bumpNote(current, deviceId), userTags: normalizeTags([...current.userTags.filter(tag => !added.includes(tag)), ...removed]) }
    } else if (before) {
      if (current.status !== after.status) throw new Error('An original note’s status changed after the merge. Undo cannot safely restore its previous status.')
      note = { ...bumpNote(current, deviceId), status: before.status, trashedAt: before.trashedAt,
        expiredAt: before.expiredAt, expiresAt: before.expiresAt, snoozedUntil: before.snoozedUntil,
        quickAccess: before.quickAccess, quickAccessAt: before.quickAccessAt }
    } else {
      // Trash is recoverable and preserves any edits made to the merged note.
      note = transitionNoteStatus(current, 'trash', deviceId)
    }
    changes.push({ note, expectedRevision: current.revisionId, reason: 'restore' as const })
  }
  const undone = { ...operation, undone: true, dismissed: true }
  await repository.commitLocalNotes(changes, { key: operationPrefix(scope) + operation.id,
    value: JSON.stringify(undone), expected: JSON.stringify(operation) })
  return { operation: undone, notes: changes.map(change => change.note) }
}
